import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { formatEur } from "../utils";
import ConfirmModal from "./ConfirmModal";

const ROOM_OPTIONS = [
  { v: "kitchen", l: "Cuisine" }, { v: "bathroom", l: "Salle de bain" },
  { v: "living", l: "Salon" }, { v: "bedroom", l: "Chambre" }, { v: "entry", l: "Entrée" }
];
const ROOM_LABELS_MAP = { kitchen: "Cuisine", bathroom: "Salle de bain", living: "Salon", bedroom: "Chambre", entry: "Entrée" };

export default function PinterestInspiration({ projectId, budget, updateBudget, materials, updateMaterials, journal, updateJournal, profile, NOW }) {
  const [boardUrl, setBoardUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ label: "", price: "", url: "", room: "living" });
  const [confirmDelete, setConfirmDelete] = useState(null); // item to delete

  useEffect(() => {
    supabase.from("project_data").select("data").eq("project_id", projectId).eq("section", "inspiration")
      .then(({ data }) => {
        if (data && data[0]?.data) { setSavedUrl(data[0].data.boardUrl || ""); setItems(data[0].data.items || []); }
        setLoading(false);
      });
  }, [projectId]);

  const save = async (url, newItems) => {
    await supabase.from("project_data").upsert(
      { project_id: projectId, section: "inspiration", data: { boardUrl: url, items: newItems }, updated_at: new Date().toISOString() },
      { onConflict: "project_id,section" }
    );
  };

  const handleSaveUrl = () => { setSavedUrl(boardUrl); setEditMode(false); save(boardUrl, items); };

  const addItem = () => {
    if (!newItem.label) return;
    const price = parseFloat(newItem.price) || 0;
    const item = { id: Date.now(), ...newItem, price, ordered: false };
    const updatedItems = [...items, item];
    setItems(updatedItems);
    save(savedUrl, updatedItems);
    if (materials && updateMaterials) {
      const mat = { id: Date.now() + 1, label: newItem.label, tag: "Pinterest", color: "#E6000030", price, validated: false, addedBy: "client", addedAt: NOW, supplierUrl: newItem.url || null, lot: null, history: [{ action: "Ajouté via Pinterest", by: "client", at: NOW }] };
      updateMaterials({ ...materials, [newItem.room]: [...(materials[newItem.room] || []), mat] });
    }
    if (journal && updateJournal) {
      const entry = { id: Date.now() + 2, role: "client", authorName: profile.name, time: "À l'instant", text: `📌 Inspiration Pinterest : "${newItem.label}"${price > 0 ? ` — ${formatEur(price)}` : ""}`, type: "pinterest", decisionStatus: null, category: null };
      updateJournal([entry, ...journal]);
    }
    if (price > 0) updateBudget({ ...budget, currentTotal: (budget.currentTotal || 0) + price });
    setNewItem({ label: "", price: "", url: "", room: "living" });
  };

  const confirmDeleteItem = (id) => {
    const item = items.find(i => i.id === id);
    setConfirmDelete(item);
  };

  const doDeleteItem = () => {
    if (!confirmDelete) return;
    const updated = items.filter(i => i.id !== confirmDelete.id);
    setItems(updated);
    save(savedUrl, updated);
    if (confirmDelete.price > 0) updateBudget({ ...budget, currentTotal: Math.max(0, (budget.currentTotal || 0) - confirmDelete.price) });
    setConfirmDelete(null);
  };

  const toggleOrder = (id) => { const updated = items.map(i => i.id === id ? { ...i, ordered: !i.ordered } : i); setItems(updated); save(savedUrl, updated); };

  if (loading) return null;

  const totalInspiration = items.reduce((s, i) => s + (i.price || 0), 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 26, fontWeight: 400 }}>Inspiration</h1>
        {totalInspiration > 0 && <span style={{ fontSize: 12, color: "#C9A96E" }}>Total : {formatEur(totalInspiration)} · intégré au budget</span>}
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div className="sl">Mon tableau Pinterest</div>
          <button className="btn" onClick={() => setEditMode(!editMode)} style={{ fontSize: 11, color: "#C9A96E", border: "1px solid #C9A96E40", borderRadius: 6, padding: "3px 10px" }}>{editMode ? "Annuler" : savedUrl ? "Modifier" : "+ Ajouter"}</button>
        </div>
        {editMode && (
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input value={boardUrl} onChange={e => setBoardUrl(e.target.value)} placeholder="https://pinterest.com/username/board-name/" style={{ flex: 1, fontSize: 12 }} />
            <button className="btn" onClick={handleSaveUrl} style={{ background: "#C9A96E", color: "#0F0E0C", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>Sauver</button>
          </div>
        )}
        {savedUrl ? (
          <div>
            <div style={{ background: "#141410", border: "1px solid #2A2924", borderRadius: 10, padding: 20, marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#E6000020", border: "1px solid #E6000040", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#E60000"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" /></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "#E8E2D8", marginBottom: 3 }}>Tableau Pinterest lié</div>
                  <div style={{ fontSize: 11, color: "#5A5450", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{savedUrl}</div>
                </div>
                <a href={savedUrl} target="_blank" rel="noopener noreferrer" style={{ background: "#E60000", color: "#fff", textDecoration: "none", borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>Ouvrir →</a>
              </div>
            </div>
            <div style={{ background: "#1A1208", border: "1px solid #2A2010", borderRadius: 8, padding: "8px 14px", fontSize: 11, color: "#7A6848" }}>📌 Pinterest bloque l'affichage direct — ouvrez le tableau puis ajoutez vos articles ci-dessous.</div>
          </div>
        ) : (
          <div style={{ background: "#141410", border: "1px dashed #2A2924", borderRadius: 10, padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>📌</div>
            <div style={{ fontSize: 13, color: "#7A7468", marginBottom: 6 }}>Ajoutez l'URL de votre tableau Pinterest</div>
            <div style={{ fontSize: 11, color: "#5A5450" }}>ex: https://pinterest.com/votrecompte/ma-renovation/</div>
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 18 }}>
        <div className="sl" style={{ marginBottom: 6 }}>Liste de souhaits</div>
        <div style={{ fontSize: 12, color: "#5A5450", marginBottom: 14 }}>Chaque article ajouté apparaît dans Matériaux et Journal, et s'intègre au budget.</div>
        {items.map(item => (
          <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", background: "#252420", borderRadius: 9, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: item.ordered ? "#5A5450" : "#E8E2D8", textDecoration: item.ordered ? "line-through" : "none" }}>{item.label}</div>
              <div style={{ fontSize: 10, color: "#5A5450" }}>{item.room ? ROOM_LABELS_MAP[item.room] : ""}</div>
              {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: "#7EB8C9" }}>Voir le produit →</a>}
            </div>
            {item.price > 0 && <span style={{ fontSize: 12, color: "#C9A96E", fontWeight: 500, flexShrink: 0 }}>{formatEur(item.price)}</span>}
            <button className="btn" onClick={() => toggleOrder(item.id)} style={{ fontSize: 10, padding: "3px 10px", borderRadius: 6, border: `1px solid ${item.ordered ? "#A8C97E40" : "#2A2924"}`, color: item.ordered ? "#A8C97E" : "#5A5450", flexShrink: 0 }}>{item.ordered ? "✓ Commandé" : "Commander"}</button>
            <button className="btn" onClick={() => confirmDeleteItem(item.id)} style={{ fontSize: 12, color: "#FC8181", border: "1px solid #FC818130", borderRadius: 6, padding: "3px 8px", flexShrink: 0 }}>✕</button>
          </div>
        ))}
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          <input value={newItem.label} onChange={e => setNewItem(p => ({ ...p, label: e.target.value }))} placeholder="Nom de l'article (ex: Robinetterie SDB dorée)" style={{ fontSize: 12 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <input type="number" value={newItem.price} onChange={e => setNewItem(p => ({ ...p, price: e.target.value }))} placeholder="Prix €" style={{ flex: 1, fontSize: 12 }} />
            <select value={newItem.room} onChange={e => setNewItem(p => ({ ...p, room: e.target.value }))} style={{ flex: 1, fontSize: 12 }}>
              {ROOM_OPTIONS.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          <input value={newItem.url} onChange={e => setNewItem(p => ({ ...p, url: e.target.value }))} placeholder="URL produit (optionnel)" style={{ fontSize: 12 }} />
          <button className="btn" onClick={addItem} style={{ background: "#C9A96E", color: "#0F0E0C", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 600, border: "none" }}>
            + Ajouter{newItem.price ? ` (+${formatEur(parseFloat(newItem.price) || 0)} au budget)` : ""}
          </button>
        </div>
      </div>

      {confirmDelete && (
        <ConfirmModal
          title="Supprimer cet article ?"
          message={`"${confirmDelete.label}"${confirmDelete.price > 0 ? ` — ${formatEur(confirmDelete.price)} sera déduit du budget.` : ""}`}
          confirmLabel="Supprimer"
          danger={true}
          onConfirm={doDeleteItem}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
